// Phase 2.D — typed clients for the customer + admin submissions APIs.
//
// Customer surface lives at /api/v2/portal/submissions and is tenant-scoped
// server-side. Admin surface lives at /api/v2/submissions and is
// cross-tenant — same response shape so the table component is reusable.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export type SubmissionStatus =
    | "pending"
    | "approved"
    | "rejected"
    | "duplicate"
    | "withdrawn";

export type SubmissionIoCType =
    | "ip"
    | "cidr"
    | "domain"
    | "email"
    | "url"
    | "certificate"
    | "hash";

export interface PortalSubmission {
    id: string;
    tenantId: string;
    tenantSlug?: string;
    submittedBy: string;
    submitterEmail?: string;
    iocType: SubmissionIoCType;
    value: string;
    severity: number;
    tags: string[];
    killchainPhase?: string | null;
    context: string;
    communityShare: boolean;
    status: SubmissionStatus;
    reviewedBy?: string | null;
    reviewerEmail?: string;
    reviewedAt?: string | null;
    reviewNote: string;
    iocId?: string | null;
    submittedAt: string;
}

export interface SubmissionListResponse {
    submissions: PortalSubmission[];
    total: number;
    limit: number;
    offset: number;
}

export interface SubmissionListFilters {
    status?: SubmissionStatus | "";
    type?: SubmissionIoCType | "";
    q?: string;
    fromDate?: string;
    toDate?: string;
    tenantId?: string; // admin-only
    limit?: number;
    offset?: number;
}

export interface SubmissionCreateInput {
    type: SubmissionIoCType;
    value: string;
    severity?: number;
    tags?: string[];
    killchainPhase?: string;
    context?: string;
    communityShare?: boolean;
}

export interface SubmissionPatchInput {
    severity?: number;
    tags?: string[];
    context?: string;
    communityShare?: boolean;
}

export interface SubmissionApproveInput {
    note?: string;
    severityOverride?: number;
    killchainPhaseOverride?: string;
    applyCommunityShare?: boolean;
}

export interface SubmissionStatsResponse {
    counts: Record<SubmissionStatus, number>;
    total: number;
}

function buildQuery(filters: SubmissionListFilters): string {
    const p = new URLSearchParams();
    if (filters.status) p.set("status", filters.status);
    if (filters.type) p.set("type", filters.type);
    if (filters.q) p.set("q", filters.q);
    if (filters.fromDate) p.set("fromDate", filters.fromDate);
    if (filters.toDate) p.set("toDate", filters.toDate);
    if (filters.tenantId) p.set("tenantId", filters.tenantId);
    if (filters.limit != null) p.set("limit", String(filters.limit));
    if (filters.offset != null) p.set("offset", String(filters.offset));
    const s = p.toString();
    return s ? `?${s}` : "";
}

// --- customer hooks ---

export function useMySubmissions(filters: SubmissionListFilters) {
    return useQuery({
        queryKey: ["portal", "submissions", "list", filters] as const,
        queryFn: () =>
            api.get<SubmissionListResponse>(`/api/v2/portal/submissions${buildQuery(filters)}`),
    });
}

export function useMySubmission(id: string | undefined) {
    return useQuery({
        queryKey: ["portal", "submissions", "detail", id] as const,
        queryFn: () =>
            api.get<{ submission: PortalSubmission }>(`/api/v2/portal/submissions/${id}`),
        enabled: !!id,
    });
}

export function useCreateSubmission() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: SubmissionCreateInput) =>
            api.post<{ submission: PortalSubmission }>(
                `/api/v2/portal/submissions`,
                input,
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["portal", "submissions"] });
        },
    });
}

export function usePatchSubmission(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: SubmissionPatchInput) =>
            api.patch<{ submission: PortalSubmission }>(
                `/api/v2/portal/submissions/${id}`,
                input,
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["portal", "submissions"] });
        },
    });
}

export function useWithdrawSubmission() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            api.post<{ submission: PortalSubmission }>(
                `/api/v2/portal/submissions/${id}/withdraw`,
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["portal", "submissions"] });
        },
    });
}

// --- admin hooks ---

export function useSubmissionsAdmin(filters: SubmissionListFilters) {
    return useQuery({
        queryKey: ["submissions", "list", filters] as const,
        queryFn: () =>
            api.get<SubmissionListResponse>(`/api/v2/submissions${buildQuery(filters)}`),
    });
}

export function useSubmissionAdmin(id: string | undefined) {
    return useQuery({
        queryKey: ["submissions", "detail", id] as const,
        queryFn: () => api.get<{ submission: PortalSubmission }>(`/api/v2/submissions/${id}`),
        enabled: !!id,
    });
}

export function useSubmissionsStats() {
    return useQuery({
        queryKey: ["submissions", "stats"] as const,
        queryFn: () => api.get<SubmissionStatsResponse>(`/api/v2/submissions/stats`),
        refetchInterval: 60_000,
    });
}

export function useApproveSubmission() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: SubmissionApproveInput }) =>
            api.post<{ submission: PortalSubmission }>(
                `/api/v2/submissions/${id}/approve`,
                input,
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["submissions"] });
        },
    });
}

export function useRejectSubmission() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, note }: { id: string; note: string }) =>
            api.post<{ submission: PortalSubmission }>(
                `/api/v2/submissions/${id}/reject`,
                { note },
            ),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["submissions"] });
        },
    });
}

// --- helpers ---

export function statusPillClass(status: SubmissionStatus): string {
    switch (status) {
        case "pending":
            return "status-pill status-pill--info";
        case "approved":
            return "status-pill status-pill--live";
        case "rejected":
            return "status-pill status-pill--danger";
        case "duplicate":
            return "status-pill status-pill--muted";
        case "withdrawn":
            return "status-pill status-pill--muted";
    }
}
